import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AUTHORS, getAuthor } from "@/lib/authors";
import { SITE } from "@/lib/site";
import { notFoundStatus } from "@/lib/http-status";
import { ogImageMeta } from "@/lib/og";

export const Route = createFileRoute("/auteurs/$slug")({
  loader: async ({ params }) => {
    const author = getAuthor(params.slug);
    if (!author) {
      await notFoundStatus();
      throw notFound();
    }
    return { author };
  },
  head: ({ loaderData }) => {
    const author = loaderData?.author;
    if (!author) return {};
    const description = `${author.name} is een van onze beste casino-experts en ${author.role.toLowerCase()}. Lees expertise over ${author.expertise.join(", ").toLowerCase()}, gecontroleerd in november 2026.`;
    return {
      meta: [
        { title: `Beste Casino-Expert: ${author.name} | Casino Betalingen` },
        { name: "description", content: description },
        { property: "og:title", content: `Beste Casino-Expert: ${author.name}` },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta(`auteur-${author.slug}`),
        { property: "og:url", content: `${SITE.url}/auteurs/${author.slug}/` },
      ],
      links: [{ rel: "canonical", href: `${SITE.url}/auteurs/${author.slug}/` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            name: author.name,
            jobTitle: author.role,
            image: author.photo,
            description: author.bio,
            worksFor: { "@id": `${SITE.url}/#organization` },
            knowsAbout: author.expertise,
          }),
        },
      ],
    };
  },
  component: AuthorPage,
});

function AuthorPage() {
  const { author } = Route.useLoaderData();
  const others = AUTHORS.filter((a) => a.slug !== author.slug).slice(0, 3);

  return (
    <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <nav aria-label="Broodkruimels" className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/auteurs/" className="hover:text-foreground">Redactie</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{author.name}</span>
      </nav>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <img src={author.photo} alt={author.name} width={112} height={112} fetchPriority="high" className="h-28 w-28 rounded-full object-cover" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{author.name}</h1>
          <p className="mt-1 text-muted-foreground">
            {author.role} · bij de redactie sinds {author.since} · profiel gecontroleerd 9 oktober 2026
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {author.expertise.map((item) => (
              <Badge key={item} variant="secondary" className="font-normal">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-8 max-w-3xl text-[15px] leading-7 text-muted-foreground">{author.bio}</p>

      <Card className="card-surface mt-8 p-5">
        <h2 className="text-sm font-semibold text-foreground">Werkwijze</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {author.name} publiceert alleen na een controle door een tweede redacteur. Testresultaten worden vastgelegd
          met datum, bedrag en apparaat, zodat elke bewering herleidbaar blijft. Commerciële afspraken hebben geen
          invloed op de conclusies.
        </p>
      </Card>

      <h2 className="mt-12 text-xl font-semibold text-foreground">Andere redacteuren</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {others.map((other) => (
          <div
            key={other.slug}
            className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary"
          >
            <img src={other.photo} alt={other.name} loading="lazy" width={40} height={40} decoding="async" className="h-10 w-10 rounded-full object-cover" />
            <span>
              <span className="block text-sm font-medium text-foreground">{other.name}</span>
              <span className="block text-xs text-muted-foreground">{other.role}</span>
            </span>
          </div>
        ))}
      </div>
          <PageImageGallery path={`/auteurs/${author.slug}`} title="Werkwijze van de redactie" />
    </main>
  );
}
